import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  QrCode, XCircle, CheckCircle, AlertTriangle, LogOut, User, 
  Wifi, WifiOff, List, X, Phone, Mail, Download, RefreshCw, Database, ChevronLeft
} from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import PasswordInput from "@/components/common/PasswordInput";

const QRScanner = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [manualCode, setManualCode] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [scannedCount, setScannedCount] = useState(0);
  const [savedCredentials, setSavedCredentials] = useState("");
  const [userName, setUserName] = useState("");
  const [cacheStatus, setCacheStatus] = useState({ loading: false, count: 0, lastUpdate: null });
  const [showCacheInfo, setShowCacheInfo] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const audioContextRef = useRef(null);
  const validateQRRef = useRef(null);
  const cacheIntervalRef = useRef(null);
  const navigate = useNavigate();

  // Check for credentials from centralized login
  useEffect(() => {
    const storedCreds = localStorage.getItem('taytantos_credentials');
    const storedRole = localStorage.getItem('taytantos_user_role');
    const storedNombre = localStorage.getItem('taytantos_user_nombre');
    
    if (storedCreds && storedRole === 'scanner') {
      setSavedCredentials(storedCreds);
      setUserName(storedNombre || '');
      setIsLoggedIn(true);
      // Clear the stored credentials after use
      localStorage.removeItem('taytantos_credentials');
      localStorage.removeItem('taytantos_user_role');
      localStorage.removeItem('taytantos_user_nombre');
    }
  }, []);

  // Initialize AudioContext
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineValidations();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load offline queue from localStorage
    const savedQueue = localStorage.getItem('taytantos_offline_queue');
    if (savedQueue) {
      setOfflineQueue(JSON.parse(savedQueue));
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Play sound effect
  const playSound = (type) => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    if (type === 'success') {
      // Pleasant success sound - two ascending tones
      oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } else if (type === 'warning') {
      // Warning sound - two same tones
      oscillator.frequency.setValueAtTime(440, ctx.currentTime); // A4
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } else if (type === 'error') {
      // Error sound - descending tone
      oscillator.frequency.setValueAtTime(349.23, ctx.currentTime); // F4
      oscillator.frequency.setValueAtTime(261.63, ctx.currentTime + 0.1); // C4
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    }
  };

  // Vibrate device
  const vibrate = (pattern) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  // Save validated QRs to localStorage for offline mode
  const saveToOfflineCache = (qrCode, result) => {
    const cache = JSON.parse(localStorage.getItem('taytantos_validated_qrs') || '{}');
    cache[qrCode] = {
      ...result,
      cached_at: new Date().toISOString()
    };
    localStorage.setItem('taytantos_validated_qrs', JSON.stringify(cache));
  };

  // Check offline cache
  const checkOfflineCache = (qrCode) => {
    const cache = JSON.parse(localStorage.getItem('taytantos_validated_qrs') || '{}');
    return cache[qrCode] || null;
  };

  // Add to offline queue for sync later
  const addToOfflineQueue = (qrCode) => {
    const newQueue = [...offlineQueue, { qrCode, timestamp: new Date().toISOString() }];
    setOfflineQueue(newQueue);
    localStorage.setItem('taytantos_offline_queue', JSON.stringify(newQueue));
  };

  // Sync offline validations when back online
  const syncOfflineValidations = async () => {
    const queue = JSON.parse(localStorage.getItem('taytantos_offline_queue') || '[]');
    if (queue.length === 0) return;

    for (const item of queue) {
      try {
        await axios.post(
          `${API}/validate-qr?qr_code=${encodeURIComponent(item.qrCode)}`,
          {},
          { headers: { Authorization: `Basic ${savedCredentials}` } }
        );
      } catch (e) {
        console.log('Failed to sync:', item.qrCode);
      }
    }

    // Clear queue after sync
    localStorage.setItem('taytantos_offline_queue', '[]');
    setOfflineQueue([]);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    const currentCredentials = btoa(`${loginForm.username}:${loginForm.password}`);
    
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      if (response.data.success) {
        setSavedCredentials(currentCredentials);
        setIsLoggedIn(true);
        // Store credentials for offline use
        localStorage.setItem('taytantos_scanner_credentials', currentCredentials);
      }
    } catch (e) {
      // Try offline login
      const savedCreds = localStorage.getItem('taytantos_scanner_credentials');
      if (savedCreds === currentCredentials) {
        setSavedCredentials(currentCredentials);
        setIsLoggedIn(true);
      } else {
        setLoginError("Credenciales incorrectas");
      }
    }
  };

  // Preload offline cache after login
  const preloadCache = useCallback(async (showLoading = true) => {
    if (!savedCredentials) return;
    
    if (showLoading) {
      setCacheStatus(prev => ({ ...prev, loading: true }));
    }
    
    try {
      const response = await axios.get(`${API}/admin/offline-cache`, {
        headers: { Authorization: `Basic ${savedCredentials}` }
      });
      
      const cache = {};
      for (const item of response.data.data) {
        cache[item.qr_code] = {
          valid: true,
          reservation: item.reservation,
          event: item.event,
          already_checked_in: item.already_checked_in,
          cached_at: new Date().toISOString()
        };
      }
      localStorage.setItem('taytantos_validated_qrs', JSON.stringify(cache));
      
      setCacheStatus({
        loading: false,
        count: response.data.count,
        lastUpdate: new Date().toISOString()
      });
      
      // Save cache status to localStorage
      localStorage.setItem('taytantos_cache_status', JSON.stringify({
        count: response.data.count,
        lastUpdate: new Date().toISOString()
      }));
      
      console.log(`✅ Caché offline actualizado: ${response.data.count} entradas`);
      return true;
    } catch (e) {
      console.log('❌ Error al cargar caché offline:', e.message);
      setCacheStatus(prev => ({ ...prev, loading: false }));
      return false;
    }
  }, [savedCredentials]);

  // Initial cache load and auto-refresh every 5 minutes
  useEffect(() => {
    if (isLoggedIn && savedCredentials) {
      // Load cached status from localStorage
      const savedStatus = localStorage.getItem('taytantos_cache_status');
      if (savedStatus) {
        const status = JSON.parse(savedStatus);
        setCacheStatus(prev => ({ ...prev, count: status.count, lastUpdate: status.lastUpdate }));
      }
      
      // Initial load
      if (isOnline) {
        preloadCache(true);
      }
      
      // Auto-refresh every 2 minutes when online
      cacheIntervalRef.current = setInterval(() => {
        if (navigator.onLine) {
          console.log('🔄 Actualizando caché automáticamente...');
          preloadCache(false);
        }
      }, 2 * 60 * 1000); // 2 minutes
      
      return () => {
        if (cacheIntervalRef.current) {
          clearInterval(cacheIntervalRef.current);
        }
      };
    }
  }, [isLoggedIn, savedCredentials, isOnline, preloadCache]);

  const validateQR = async (qrCode) => {
    // Online validation
    if (isOnline) {
      try {
        console.log("Validating QR:", qrCode);
        console.log("Using credentials:", savedCredentials ? "saved" : "none");
        
        const response = await axios.post(
          `${API}/validate-qr?qr_code=${encodeURIComponent(qrCode)}`,
          {},
          { headers: { Authorization: `Basic ${savedCredentials}` } }
        );
        
        const result = response.data;
        console.log("Validation result:", result);
        setScanResult(result);
        setScanning(false);
        
        // Save to cache for offline use
        saveToOfflineCache(qrCode, result);
        
        // Sound and vibration feedback
        if (result.valid && !result.already_checked_in) {
          playSound('success');
          vibrate([100, 50, 100]); // Short double vibration for success
          setScannedCount(prev => prev + 1); // Increment scanned counter
        } else if (result.already_checked_in) {
          playSound('error');
          vibrate([500]); // Long vibration for already validated
        } else {
          playSound('error');
          vibrate([500]); // Long vibration for error
        }
      } catch (e) {
        console.error("Validation error:", e.response?.data || e.message);
        setScanResult({
          valid: false,
          message: "Error al validar el código: " + (e.response?.data?.detail || e.message)
        });
        playSound('error');
        vibrate([500]); // Long vibration for error
        setScanning(false);
      }
    } else {
      // Offline validation
      const cached = checkOfflineCache(qrCode);
      
      if (cached) {
        if (cached.already_checked_in) {
          setScanResult({
            valid: true,
            message: "⚠️ Esta entrada ya fue validada (modo offline)",
            reservation: cached.reservation,
            event: cached.event,
            already_checked_in: true
          });
          playSound('warning');
          vibrate([200]);
        } else {
          // Mark as checked in offline
          cached.already_checked_in = true;
          cached.offline_validated = true;
          saveToOfflineCache(qrCode, cached);
          addToOfflineQueue(qrCode);
          
          setScanResult({
            valid: true,
            message: "✅ Entrada válida (modo offline - se sincronizará)",
            reservation: cached.reservation,
            event: cached.event,
            already_checked_in: false
          });
          playSound('success');
          vibrate([100, 50, 100]);
          setScannedCount(prev => prev + 1); // Increment scanned counter
        }
      } else {
        // QR not in cache
        setScanResult({
          valid: false,
          message: "❌ No se puede validar - Sin conexión y entrada no cacheada"
        });
        playSound('error');
        vibrate([100, 100, 100, 100, 100]);
      }
      setScanning(false);
    }
  };

  // Keep ref updated with latest validateQR function
  validateQRRef.current = validateQR;

  const handleManualValidation = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      validateQR(manualCode.trim());
    }
  };

  const resetScanner = () => {
    setScanResult(null);
    setManualCode("");
    setTimeout(() => {
      setScanning(true);
    }, 100);
  };

  useEffect(() => {
    let mounted = true;
    
    if (isLoggedIn && scanning && !html5QrCodeRef.current) {
      const initScanner = async () => {
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const element = document.getElementById("qr-reader");
        if (!element || !mounted) return;
        
        try {
          const { Html5Qrcode } = await import('html5-qrcode');
          
          if (!document.getElementById("qr-reader") || !mounted) return;
          
          const html5QrCode = new Html5Qrcode("qr-reader");
          html5QrCodeRef.current = html5QrCode;
          
          // Auto-start with back camera, no user interaction needed
          await html5QrCode.start(
            { facingMode: "environment" },
            {
              fps: 15,
              qrbox: { width: 280, height: 280 },
              aspectRatio: 1.0
            },
            (decodedText) => {
              // Auto-validate immediately when QR is detected
              console.log("QR detected by camera:", decodedText);
              html5QrCode.stop().then(() => {
                html5QrCodeRef.current = null;
                // Use ref to get the latest validateQR function
                if (validateQRRef.current) {
                  validateQRRef.current(decodedText);
                }
              }).catch(() => {
                html5QrCodeRef.current = null;
                if (validateQRRef.current) {
                  validateQRRef.current(decodedText);
                }
              });
            },
            () => {} // Ignore scan errors
          );
        } catch (err) {
          console.log("Camera error:", err);
        }
      };
      
      initScanner();
    }

    return () => {
      mounted = false;
      if (html5QrCodeRef.current) {
        try {
          html5QrCodeRef.current.stop().catch(() => {});
        } catch (e) {}
        html5QrCodeRef.current = null;
      }
    };
  }, [isLoggedIn, scanning]);

  if (!isLoggedIn) {
    return (
      <div className="hero-bg min-h-screen flex items-center justify-center">
        <LaserBackground />
        <div className="form-section max-w-md w-full mx-4 relative z-10">
          <div className="text-center mb-6">
            <QrCode size={48} className="text-[var(--primary)] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-white uppercase">
              Escáner de Entradas
            </h2>
          </div>
          
          {loginError && (
            <div className="error-message mb-4">{loginError}</div>
          )}
          
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Usuario</label>
              <input
                type="text"
                className="custom-input"
                value={loginForm.username}
                onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                data-testid="scanner-username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <PasswordInput
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                testId="scanner-password"
              />
            </div>
            <button type="submit" className="btn-primary mt-4" data-testid="scanner-login-btn">
              Acceder
            </button>
          </form>
          
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-4 text-[var(--text-muted)] hover:text-white text-sm"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10">
        {/* Online/Offline Status */}
        <div className={`flex items-center justify-center gap-2 py-2 mb-4 text-sm font-bold uppercase tracking-wide ${
          isOnline 
            ? 'bg-[var(--success)] text-black' 
            : 'bg-yellow-500 text-black'
        }`} data-testid="connection-status">
          {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
          {isOnline ? 'ONLINE' : 'OFFLINE - Modo sin conexión'}
        </div>

        {/* Offline Queue Indicator */}
        {offlineQueue.length > 0 && (
          <div className="text-center mb-4 text-yellow-500 text-sm">
            {offlineQueue.length} validación(es) pendiente(s) de sincronizar
          </div>
        )}

        {/* Cache Status Bar */}
        <div 
          className="flex items-center justify-between bg-[var(--surface)] px-4 py-2 mb-4 cursor-pointer"
          onClick={() => setShowCacheInfo(!showCacheInfo)}
        >
          <div className="flex items-center gap-2">
            <Database size={16} className={cacheStatus.loading ? "animate-pulse text-yellow-500" : "text-[var(--accent-cyan)]"} />
            <span className="text-sm text-[var(--text-muted)]">
              {cacheStatus.loading 
                ? "Descargando entradas..." 
                : `${cacheStatus.count} entradas en caché`}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isOnline && !cacheStatus.loading) {
                preloadCache(true);
              }
            }}
            disabled={!isOnline || cacheStatus.loading}
            className={`p-2 rounded ${isOnline && !cacheStatus.loading ? 'hover:bg-white/10' : 'opacity-50'}`}
            title="Actualizar caché"
          >
            <RefreshCw size={16} className={`text-[var(--accent-cyan)] ${cacheStatus.loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Cache Info Panel */}
        {showCacheInfo && (
          <div className="bg-[var(--surface)] border border-[var(--accent-cyan)]/30 p-4 mb-4 text-sm">
            <div className="flex justify-between mb-2">
              <span className="text-[var(--text-muted)]">Entradas cacheadas:</span>
              <span className="text-white font-bold">{cacheStatus.count}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-[var(--text-muted)]">Última actualización:</span>
              <span className="text-white">
                {cacheStatus.lastUpdate 
                  ? new Date(cacheStatus.lastUpdate).toLocaleTimeString('es-ES')
                  : 'Nunca'}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-[var(--text-muted)]">Auto-actualización:</span>
              <span className="text-[var(--success)]">Cada 2 minutos</span>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-3 pt-3 border-t border-white/10">
              💡 Las entradas se descargan automáticamente para funcionar sin conexión.
              Puedes escanear aunque pierdas el WiFi.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Volver</span>
          </button>
          <div className="text-right">
            <h1 className="font-logo text-xl">
              <span className="text-white neon-glow">TAYTANTOS</span>
              <span className="text-white neon-glow text-base" style={{fontFamily: "'Monoton', cursive"}}>pass</span>
            </h1>
          </div>
        </div>

        <div className="text-center mb-6">
          <QrCode size={32} className="text-[var(--primary)] mx-auto mb-2" />
          <h2 className="font-heading text-xl text-white uppercase">Escáner de Entradas</h2>
          <div className="mt-4 bg-[var(--surface)] p-4 inline-block">
            <span className="text-[var(--text-muted)] text-sm uppercase tracking-wide">Entradas escaneadas: </span>
            <span className="font-mono text-2xl text-[var(--success)] font-bold">{scannedCount}</span>
          </div>
        </div>

        {scanning ? (
          <>
            {/* QR Scanner - Auto validates on detection */}
            <div className="scanner-container mb-4" data-testid="qr-scanner">
              <div id="qr-reader" ref={scannerRef}></div>
            </div>
            
            <p className="text-[var(--accent-cyan)] text-sm text-center mb-6 animate-pulse">
              Apunta la cámara al código QR - se validará automáticamente
            </p>

            {/* Manual Input as fallback */}
            <div className="form-section">
              <p className="text-[var(--text-muted)] text-xs mb-3 text-center uppercase tracking-wide">
                O introduce el código manualmente:
              </p>
              <form onSubmit={handleManualValidation} className="flex gap-2">
                <input
                  type="text"
                  className="custom-input flex-grow"
                  placeholder="TAYTANTOS-xxxxx..."
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  data-testid="manual-qr-input"
                />
                <button type="submit" className="btn-primary w-auto px-6" data-testid="manual-validate-btn">
                  OK
                </button>
              </form>
            </div>
          </>
        ) : scanResult ? (
          /* Result */
          <div className="form-section text-center" data-testid="scan-result">
            {scanResult.valid ? (
              scanResult.already_checked_in ? (
                <>
                  <AlertTriangle size={64} className="text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-heading text-2xl text-yellow-500 uppercase mb-2">
                    Ya Validada
                  </h3>
                  <p className="text-[var(--text-muted)] mb-6">{scanResult.message}</p>
                </>
              ) : (
                <>
                  <CheckCircle size={64} className="text-[var(--success)] mx-auto mb-4" />
                  <h3 className="font-heading text-2xl text-[var(--success)] uppercase mb-2">
                    Acceso Permitido
                  </h3>
                  <p className="text-[var(--text-muted)] mb-6">{scanResult.message}</p>
                </>
              )
            ) : (
              <>
                <XCircle size={64} className="text-[var(--error)] mx-auto mb-4" />
                <h3 className="font-heading text-2xl text-[var(--error)] uppercase mb-2">
                  Entrada Inválida
                </h3>
                <p className="text-[var(--text-muted)] mb-6">{scanResult.message}</p>
              </>
            )}

            {scanResult.reservation && (
              <div className="qr-info text-left mb-6">
                <div className="qr-info-row">
                  <span className="qr-info-label">Nombre</span>
                  <span className="qr-info-value">{scanResult.reservation.nombre}</span>
                </div>
                <div className="qr-info-row">
                  <span className="qr-info-label">Email</span>
                  <span className="qr-info-value">{scanResult.reservation.email}</span>
                </div>
                {scanResult.event && (
                  <>
                    <div className="qr-info-row">
                      <span className="qr-info-label">Evento</span>
                      <span className="qr-info-value">{scanResult.event.nombre}</span>
                    </div>
                    <div className="qr-info-row">
                      <span className="qr-info-label">Fecha</span>
                      <span className="qr-info-value">{scanResult.event.fecha}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            <button 
              onClick={resetScanner}
              className="btn-primary"
              data-testid="scan-again-btn"
            >
              Escanear Otra Entrada
            </button>
          </div>
        ) : (
          /* Loading state while validating */
          <div className="form-section text-center" data-testid="scan-loading">
            <div className="animate-spin w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className="font-heading text-xl text-[var(--text-muted)] uppercase">
              Validando...
            </h3>
          </div>
        )}
      </div>
    </div>
  );
};

// Privacy Policy Page

export default QRScanner;
